type OnPositionUpdateCallback = (position: number) => void;
type OnEventCallback = () => void;

export class AudioPlayerCanvasWrapper {
  private audioContext: AudioContext | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private lastURL: string | null = null;

  private prepared: boolean = false;
  private playLocation: number = 0;
  private duration: number = 0;
  private waveformData: number[] = [];
  private isDragging: boolean = false;

  private backgroundColor: string = 'white';
  private waveformColor: string = 'black';
  private playedWaveformColor: string = 'black';
  private waveformWidth: number = 1;
  private playedWaveformWidth: number = 2;
  private waveformHeightFactor: number = 0.4;
  private smoothingFactor: number = 0.85;

  private canvasWidth: number | null = null;
  private canvasHeight: number | null = null;

  private onUpdateCallbacks: Set<OnPositionUpdateCallback>;
  private onClickBeginCallbacks: Set<OnEventCallback>;

  constructor() {
    this.onUpdateCallbacks = new Set();
    this.onClickBeginCallbacks = new Set();
  }

  ///////////////////////////////////////
  // Lifecycles
  ///////////////////////////////////////
  destroy() {
    this.clearCallbacks();
    this.clear();
    this.detachEventListeners();
    this.canvas = null;
    this.audioContext?.close();
    this.audioContext = null;
  }

  clear() {
    this.waveformData = [];
    this.playLocation = 0;
    this.duration = 0;
    this.prepared = false;
  }

  ///////////////////////////////////////
  // API
  ///////////////////////////////////////
  setCanvas(canvas: HTMLCanvasElement | null) {
    this.detachEventListeners();
    this.canvas = canvas;
    this.attachEventListeners();
  }

  drawOnce() {
    if (!this.prepared || !this.canvas) return;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    const width = this.canvasWidth ?? 0;
    const height = this.canvasHeight ?? 0;

    const backgroundColor = this.backgroundColor;
    const waveformColor = this.waveformColor;
    const playedWaveformColor = this.playedWaveformColor;

    const waveformWidth = this.waveformWidth ?? 1;
    const playedWaveformWidth = this.playedWaveformWidth ?? 2;

    const waveformHeightFactor = this.waveformHeightFactor ?? 0.85;
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    let x = 0;
    const startY = height * 0.8;
    const sliceWidth = width / this.waveformData.length;
    const progressIndex = Math.floor((this.playLocation / this.duration) * this.waveformData.length);

    ctx.lineWidth = playedWaveformWidth;
    ctx.strokeStyle = playedWaveformColor;
    ctx.beginPath();

    for (let i = 0; i < progressIndex && i < this.waveformData.length; i++) {
      const v = this.waveformData[i];
      const y = startY - v * (height * waveformHeightFactor);
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
      x += sliceWidth;
    }
    ctx.stroke();

    ctx.lineWidth = waveformWidth;
    ctx.strokeStyle = waveformColor;
    ctx.beginPath();
    if (progressIndex < this.waveformData.length) {
      if (progressIndex > 0) {
        const v = this.waveformData[progressIndex - 1];
        const y = startY - v * (height * waveformHeightFactor);
        ctx.moveTo(x - sliceWidth, y);
      } else {
        ctx.moveTo(0, startY);
      }

      for (let i = Math.max(0, progressIndex); i < this.waveformData.length; i++) {
        const v = this.waveformData[i];
        const y = startY - v * (height * waveformHeightFactor);
        ctx.lineTo(x, y);
        x += sliceWidth;
      }
      ctx.stroke();
    }
  }

  async readAudio(url: string) {
    if (!this.audioContext || !this.canvas) return;
    if (this.lastURL === url) return;

    const response = await fetch(url);
    if (!response.ok) return;

    const arrBuf = await response.arrayBuffer();
    const audioBuf = await this.audioContext.decodeAudioData(arrBuf);

    this.duration = audioBuf.duration;

    const data = audioBuf.getChannelData(0);
    const samples = this.canvasWidth ?? this.canvas.width;
    const blockSize = Math.floor(data.length / samples);
    const filteredData: number[] = [];

    for (let i = 0; i < samples; i++) {
      let blockSum = 0;
      for (let j = 0; j < blockSize; j++) {
        blockSum += Math.abs(data[blockSize * i + j]);
      }
      filteredData.push(blockSum / blockSize);
    }

    let normalizedData = filteredData.map((v) => v / Math.max(...filteredData));
    normalizedData = smoothWaveformData(normalizedData, this.smoothingFactor);
    this.waveformData = normalizedData;
    this.prepared = true;
    this.lastURL = url;
    this.drawOnce();
  }

  ///////////////////////////////////////
  // Setters trigger another draw
  ///////////////////////////////////////
  setAudioContext(context: AudioContext) {
    this.audioContext = context;
  }

  setPlayLocation(loc: number) {
    this.playLocation = loc;
    this.drawOnce();
  }

  setBackgroundColor(color: string) {
    this.backgroundColor = color;
    this.drawOnce();
  }

  setWaveformColor(color: string) {
    this.waveformColor = color;
    this.drawOnce();
  }

  setWaveformWidth(waveformWidth: number) {
    this.waveformWidth = waveformWidth;
    this.drawOnce();
  }

  setPlayedWaveformWidth(waveformWidth: number) {
    this.playedWaveformWidth = waveformWidth;
    this.drawOnce();
  }

  setPlayedWaveformColor(color: string) {
    this.playedWaveformColor = color;
    this.drawOnce();
  }

  setWaveformHeightFactor(waveformHeightFactor: number) {
    this.waveformHeightFactor = waveformHeightFactor;
    this.drawOnce();
  }

  setCanvasWidth(canvasWidth: number) {
    if (this.canvasWidth !== canvasWidth) {
      this.canvasWidth = canvasWidth;
      this.drawOnce();
    }
  }

  setCanvasHeight(canvasHeight: number) {
    if (this.canvasHeight !== canvasHeight) {
      this.canvasHeight = canvasHeight;
      this.drawOnce();
    }
  }

  setSmoothingFactor(value: number) {
    if (this.smoothingFactor !== value) {
      this.smoothingFactor = value;
      this.drawOnce();
    }
  }

  ///////////////////////////////////////
  // Getters
  ///////////////////////////////////////
  getCanvasWidth() {
    return this.canvasWidth;
  }

  getCanvasHeight() {
    return this.canvasHeight;
  }

  ///////////////////////////////////////
  // Event Listeners
  ///////////////////////////////////////
  private handleMouseDown(event: MouseEvent) {
    if (!this.canvas || !this.prepared) return;

    const position = this.calculatePlayLocation(event);

    if (position) {
      this.setPlayLocation(position);
      this.onUpdateCallbacks.forEach((cb) => cb(this.playLocation));
    }

    this.isDragging = true;
  }

  private handleMouseUp() {
    if (!this.isDragging) return;
    this.isDragging = false;
    document.removeEventListener('mousedown', this.handleMouseUp.bind(this));
  }

  private handleMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;

    const position = this.calculatePlayLocation(event);
    if (position) {
      this.setPlayLocation(position);
      this.onUpdateCallbacks.forEach((cb) => cb(this.playLocation));
    }
  }

  private calculatePlayLocation(event: MouseEvent) {
    if (!this.canvas || isNaN(this.duration)) return;
    const rect = this.canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const position = Math.max(0, Math.min((clickX / this.canvas.width) * this.duration, this.duration));
    if (isNaN(position) || !isFinite(position)) return;
    return position;
  }

  private attachEventListeners() {
    if (this.canvas) {
      this.canvas.addEventListener('mousedown', this.handleMouseDown.bind(this));
      document.addEventListener('mousemove', this.handleMouseMove.bind(this));
      document.addEventListener('mouseup', this.handleMouseUp.bind(this));
      document.addEventListener('mouseleave', this.handleMouseUp.bind(this));
    }
  }

  private detachEventListeners() {
    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this.handleMouseDown.bind(this));
      document.removeEventListener('mousemove', this.handleMouseMove.bind(this));
      document.removeEventListener('mouseup', this.handleMouseUp.bind(this));
      document.removeEventListener('mouseleave', this.handleMouseUp.bind(this));
    }
  }

  ///////////////////////////////////////
  // Add / Clear Callbacks
  ///////////////////////////////////////
  addOnClickCallback(callback: OnEventCallback) {
    this.onClickBeginCallbacks.add(callback);
  }

  addOnLocationUpdateCallback(callback: OnPositionUpdateCallback) {
    this.onUpdateCallbacks.add(callback);
  }

  clearCallbacks() {
    this.onClickBeginCallbacks.clear();
    this.onUpdateCallbacks.clear();
  }
}

function smoothWaveformData(data: number[], smoothingFactor: number): number[] {
  if (smoothingFactor <= 0 || smoothingFactor >= 1) {
    return [...data]; // No smoothing if factor is invalid
  }

  const smoothedData: number[] = [];
  let previousValue = 0;

  for (let i = 0; i < data.length; i++) {
    const currentValue = data[i];
    const smoothedValue = previousValue * smoothingFactor + currentValue * (1 - smoothingFactor);
    smoothedData.push(smoothedValue);
    previousValue = smoothedValue;
  }

  return smoothedData;
}
